import { type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { axis, segments } from "./scale";
import { useWidth } from "./use-width";

export interface Series {
  key: string;
  label: string;
  points: readonly (number | null)[];
}

const COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)"];
const MARGIN = { top: 8, right: 12, bottom: 24, left: 52 };

/**
 * Values over time on one axis (dataviz: never two): up to four series in the fixed slot order,
 * and optionally a band behind the first, e.g. the middle half per garage. A null is a gap. The
 * crosshair finds the nearest period and the tooltip lists every series there; the arrow keys
 * move it too.
 */
export function TimeSeries({
  periods,
  series,
  band,
  formatValue,
  formatPeriod = (p) => p,
  height = 200,
  label,
  integer = false,
}: {
  periods: readonly string[];
  series: readonly Series[];
  band?: { label: string; low: readonly (number | null)[]; high: readonly (number | null)[] };
  formatValue: (value: number) => string;
  formatPeriod?: (period: string) => string;
  height?: number;
  label: string;
  /** Counts: whole-number ticks. */
  integer?: boolean;
}) {
  const { t } = useTranslation();
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const plotWidth = Math.max(10, width - MARGIN.left - MARGIN.right);
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const all = [...series.flatMap((s) => s.points), ...(band?.high ?? [])].filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  const { top, ticks } = axis(Math.max(0, ...all), { integer });
  const step = periods.length > 1 ? plotWidth / (periods.length - 1) : 0;
  const x = (i: number) => MARGIN.left + (periods.length > 1 ? i * step : plotWidth / 2);
  const y = (v: number) => MARGIN.top + plotHeight - (v / top) * plotHeight;
  // Labels on the x axis: the first, the last and a few between that fit.
  const every = Math.max(1, Math.ceil(periods.length / Math.max(2, Math.floor(plotWidth / 90))));

  const bandPath = (() => {
    if (!band) return null;
    const indices = periods
      .map((_, i) => i)
      .filter((i) => typeof band.low[i] === "number" && typeof band.high[i] === "number");
    if (indices.length < 2) return null;
    const upper = indices.map((i) => `${x(i)},${y(band.high[i] as number)}`);
    const lower = [...indices].reverse().map((i) => `${x(i)},${y(band.low[i] as number)}`);
    return `M${upper.join("L")}L${lower.join("L")}Z`;
  })();

  /** What the crosshair shows, in words, for screen readers. */
  const readout = (i: number) =>
    [
      formatPeriod(periods[i] ?? ""),
      ...series.map((s) => {
        const value = s.points[i];
        return `${s.label} ${typeof value === "number" ? formatValue(value) : "–"}`;
      }),
    ].join(", ");

  const pick = (clientX: number, left: number) => {
    if (periods.length === 0) return;
    const i = Math.round((clientX - left - MARGIN.left) / (step || 1));
    setActive(Math.max(0, Math.min(periods.length - 1, i)));
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const next = (active ?? periods.length - 1) + (event.key === "ArrowLeft" ? -1 : 1);
    setActive(Math.max(0, Math.min(periods.length - 1, next)));
  };

  return (
    <div ref={ref} className="relative">
      {series.length > 1 || band ? (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {series.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span className="h-0.5 w-3 rounded-full" style={{ background: COLORS[i] }} />
              {s.label}
            </li>
          ))}
          {band ? (
            <li className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-3 rounded-sm"
                style={{ background: COLORS[0], opacity: 0.15 }}
              />
              {band.label}
            </li>
          ) : null}
        </ul>
      ) : null}
      <div
        role="slider"
        aria-label={t("adminApp.chart.keysHint", { label })}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, periods.length - 1)}
        aria-valuenow={active ?? Math.max(0, periods.length - 1)}
        aria-valuetext={readout(active ?? periods.length - 1)}
        tabIndex={0}
        className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect().left)}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive(periods.length - 1)}
        onBlur={() => setActive(null)}
        onKeyDown={onKey}
      >
        <svg aria-hidden width={width} height={height} className="block">
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotWidth}
                y1={y(tick)}
                y2={y(tick)}
                stroke={tick === 0 ? "var(--chart-axis)" : "var(--border)"}
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 8}
                y={y(tick)}
                dy="0.32em"
                textAnchor="end"
                className="fill-muted-foreground text-[11px] tabular"
              >
                {formatValue(tick)}
              </text>
            </g>
          ))}
          {periods.map((period, i) =>
            // The last label ends at its period, so the one before needs room for its whole width.
            (i % every === 0 && x(periods.length - 1) - x(i) >= 84) || i === periods.length - 1 ? (
              <text
                key={period}
                x={x(i)}
                y={height - 6}
                textAnchor={i === 0 ? "start" : i === periods.length - 1 ? "end" : "middle"}
                className="fill-muted-foreground text-[11px]"
              >
                {formatPeriod(period)}
              </text>
            ) : null,
          )}
          {bandPath ? <path d={bandPath} fill={COLORS[0]} opacity={0.12} /> : null}
          {series.map((s, si) =>
            segments(s.points).map((run) =>
              run.values.length === 1 ? (
                <circle
                  key={`${s.key}-${run.start}`}
                  cx={x(run.start)}
                  cy={y(run.values[0] ?? 0)}
                  r={3}
                  fill={COLORS[si]}
                />
              ) : (
                <polyline
                  key={`${s.key}-${run.start}`}
                  points={run.values.map((v, i) => `${x(run.start + i)},${y(v)}`).join(" ")}
                  fill="none"
                  stroke={COLORS[si]}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ),
            ),
          )}
          {active !== null ? (
            <g>
              <line
                x1={x(active)}
                x2={x(active)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke="var(--muted-foreground)"
                strokeWidth={1}
              />
              {series.map((s, si) => {
                const value = s.points[active];
                return typeof value === "number" ? (
                  <circle
                    key={s.key}
                    cx={x(active)}
                    cy={y(value)}
                    r={4}
                    fill={COLORS[si]}
                    stroke="var(--surface)"
                    strokeWidth={2}
                  />
                ) : null;
              })}
            </g>
          ) : null}
        </svg>
      </div>
      {active !== null ? (
        <div
          role="status"
          className="pointer-events-none absolute top-0 z-10 min-w-36 rounded-lg border border-border bg-surface-raised px-3 py-2 text-xs shadow-md"
          style={
            x(active) > width / 2 ? { right: width - x(active) + 12 } : { left: x(active) + 12 }
          }
        >
          <p className="mb-1 font-medium text-muted-foreground">
            {formatPeriod(periods[active] ?? "")}
          </p>
          {series.map((s, si) => {
            const value = s.points[active];
            return (
              <p key={s.key} className="flex items-center gap-2">
                <span className="h-0.5 w-3 rounded-full" style={{ background: COLORS[si] }} />
                <span className="font-semibold tabular">
                  {typeof value === "number" ? formatValue(value) : "–"}
                </span>
                <span className="text-muted-foreground">{s.label}</span>
              </p>
            );
          })}
          {band && typeof band.low[active] === "number" && typeof band.high[active] === "number" ? (
            <p className="mt-0.5 text-muted-foreground">
              {band.label}: {formatValue(band.low[active] as number)}–
              {formatValue(band.high[active] as number)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
